import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../lib/api";

const MIN_SHELF_DURATION_SECONDS = 10 * 60;

function formatPublishedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("en", { month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}

function formatVideoDuration(value) {
  if (!Number.isFinite(value) || value <= 0) return "";
  const total = Math.floor(value);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}` : `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function VideoShelf({ onOpenVideo, className = "", source = "theo", refreshKey }) {
  const history = source === "history";
  const titleId = history ? "history-video-shelf-title" : "theo-video-shelf-title";
  const [catalog, setCatalog] = useState({ status: "loading", videos: [], message: "" });
  const shelfRef = useRef(null);

  const loadCatalog = useCallback(async () => {
    setCatalog((current) => ({ ...current, status: "loading", message: "" }));
    try {
      // The query revision bypasses older browser/CDN responses from before
      // the shelf became long-form only. Keep a client guard as well.
      const result = await apiRequest(history ? "/api/theofast/history" : "/api/theofast/videos?longForm=1", { cache: "no-store" });
      const videos = (result.videos || []).filter((video) => Number.isFinite(video.durationSeconds) && video.durationSeconds >= (history ? 1 : MIN_SHELF_DURATION_SECONDS));
      setCatalog({ status: "ready", videos, message: "" });
    } catch (error) {
      setCatalog({ status: "error", videos: [], message: error.message || "Videos could not be loaded right now." });
    }
  }, [history]);

  useEffect(() => { loadCatalog(); }, [loadCatalog, refreshKey]);

  const scrollShelf = (direction) => shelfRef.current?.scrollBy({ left: direction * Math.max(320, shelfRef.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <section className={`video-shelf ${className}`.trim()} aria-labelledby={titleId}>
      <div className="video-shelf-heading">
        <div><span className="eyebrow">{history ? "Recently shortened on TheoFast" : "From Theo’s channel"}</span><h2 id={titleId}>{history ? "Latest videos" : "Theo’s latest videos"}</h2></div>
        <div className="video-shelf-actions">
          {catalog.status === "ready" && <span className="video-shelf-count">{catalog.videos.length} videos</span>}
          <button type="button" onClick={() => scrollShelf(-1)} aria-label="Scroll videos left" title="Scroll left"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
          <button type="button" onClick={() => scrollShelf(1)} aria-label="Scroll videos right" title="Scroll right"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></button>
        </div>
      </div>

      {catalog.status === "loading" && <div className="video-shelf-track video-shelf-track--loading" aria-label={history ? "Loading TheoFast history" : "Loading Theo’s videos"} aria-busy="true">
        {Array.from({ length: 6 }, (_, index) => <div className="video-card-skeleton" key={index}><span /><i /><i /></div>)}
      </div>}
      {catalog.status === "error" && <div className="video-shelf-message" role="status"><p>{catalog.message}</p><button type="button" onClick={loadCatalog}>Try again</button></div>}
      {catalog.status === "ready" && catalog.videos.length === 0 && <div className="video-shelf-message" role="status"><p>{history ? "Shortened videos will appear here." : "No recent uploads were found."}</p></div>}
      {catalog.status === "ready" && catalog.videos.length > 0 && <div className="video-shelf-track" ref={shelfRef} tabIndex="0" aria-label={history ? "Recently shortened videos; scroll horizontally" : "Theo’s latest YouTube videos; scroll horizontally"}>
        {catalog.videos.map((video) => {
          const hasTheoFastDuration = Number.isFinite(video.theoFastDurationSeconds)
            && video.theoFastDurationSeconds > 0
            && video.theoFastDurationSeconds < video.durationSeconds;
          const publishedLabel = formatPublishedDate(history ? video.shortenedAt : video.publishedAt);
          return (
            <button className="video-shelf-card" key={video.videoId} type="button" onClick={() => onOpenVideo(video.videoId)} aria-label={`Open ${video.title}`}>
              <span className="video-shelf-thumbnail"><img src={video.thumbnailUrl} alt="" loading="lazy" /><span className="video-shelf-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m8 5 11 7-11 7z" /></svg></span>{video.durationSeconds > 0 && <span className={`video-shelf-duration ${hasTheoFastDuration ? "has-theofast" : ""}`} aria-label={hasTheoFastDuration ? `Original ${formatVideoDuration(video.durationSeconds)}, TheoFast ${formatVideoDuration(video.theoFastDurationSeconds)}` : undefined}>{hasTheoFastDuration ? <><s>{formatVideoDuration(video.durationSeconds)}</s><strong>{formatVideoDuration(video.theoFastDurationSeconds)}</strong></> : formatVideoDuration(video.durationSeconds)}</span>}</span>
              <span className="video-shelf-card-title">{video.title}</span>
              {publishedLabel && <span className="video-shelf-date">{publishedLabel}</span>}
            </button>
          );
        })}
      </div>}
    </section>
  );
}
